import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray
} from '@angular/forms';
import { Router } from '@angular/router';
import { 
  AuthenticationService, 
  DataService, 
  DocumentService, 
  MedicalTestService,
  PharmacyService 
} from '../../services';
import { first, map, mergeMap } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { Drug, FilterDetails, MedicalTest, QuantityDetails, User } from '../../models';
import { ImageTransform, base64ToFile } from 'ngx-image-cropper';
import { ImageCropperComponent } from '../../components/image-cropper/image-cropper.component';
import { countries } from 'countries-list';

@Component({
  selector: 'app-add-edit-product',
  templateUrl: './add-edit-product.component.html',
  styleUrls: ['./add-edit-product.component.scss']
})
export class AddEditProductComponent implements OnInit {
  @ViewChild('imageCopperElement') imageCopperElement: ImageCropperComponent;
  addProductForm: FormGroup;
  isAddDrug: boolean;
  submitted = false;
  loggedInUser: User;
  categories: FilterDetails[] = [];
  pharmacyCategories: FilterDetails[] = [];
  medicalTest: MedicalTest;
  editProduct: MedicalTest | Drug;
  isAllowedImage: boolean = true;
  uploadedImages: File[]= [];
  imageChangedEvent: any;
  croppedImages: string[] = [];
  existingImages: string[] = [];
  imagesToDelete: string[] = [];
  existingImageFiles: File[] = [];
  canvasRotation = 0;
  rotation?: number;
  translateH = 0;
  scale = 1;
  aspectRatio = 1;
  maintainAspectRatio: boolean = true;
  resizeToWidth: number = 600;
  cropperMinWidth: number = 600;
  onlyScaleDown: boolean = true;
  roundCropper: boolean = false;
  alignImage: 'left' | 'center' = 'center';
  showCropper = false;
  containWithinAspectRatio = false;
  transform: ImageTransform = {};
  imageURL?: string;
  loading: boolean = false;
  cropper = {
    x1: 0,
    y1: 0,
    x2: 600,
    y2: 400
  };
  isSuperAdmin: boolean;
  quantityTypeOptions: QuantityDetails[] = [];
  localQuantityNames: QuantityDetails[] = [];
  backendQuantityNames: QuantityDetails[] = [];
  countryNames: string[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private medicalTestService: MedicalTestService,
    private dataService: DataService,
    private documentService: DocumentService,
    private pharmacyService: PharmacyService
    ) { }

  ngOnInit(): void {
    if (this.authenticationService.currentUser()) {
      this.loggedInUser = this.authenticationService.currentUser()['user'];
      this.isSuperAdmin = this.loggedInUser.role.name === 'Admin';
    }
    this.countryNames = Object.values(countries).map(country => country.name);
    this.isAddDrug = history.state.isAddDrug;
    this.dataService.getAllLocalQuantityNames().subscribe(quantityTypes => {
      this.backendQuantityNames = quantityTypes;
      this.localQuantityNames = this.dataService.getLocalQuantityNamesLabels();
      this.quantityTypeOptions = [...this.localQuantityNames, ...this.backendQuantityNames]
        .reduce((uniqueOptions: { name: string; id?: number }[], option) => {
          if (option.name && !uniqueOptions.some(uniqueOption => uniqueOption.name === option.name)) {
            uniqueOptions.push({ name: option.name, id: option.id });
          }
        return uniqueOptions;
      }, []);
    });
    this.categories = this.dataService.getLocalFilterDetails();
    this.pharmacyCategories = this.dataService.getPharmacyFilterDetails();
    this.dataService.getProductToEdit().subscribe(product => {
      this.editProduct = product as MedicalTest | Drug;
      if (this.editProduct && this.editProduct.images) {
        this.existingImages = this.editProduct.images as string[];
      }
    });
    const duration = !this.isAddDrug && (this.editProduct as MedicalTest)?.duration?.split(' ');
    this.addProductForm = this.formBuilder.group({
      name: [this.editProduct
        ? this.isAddDrug
          ? (this.editProduct as Drug).name
          : (this.editProduct as MedicalTest).name
          : '',
        Validators.required
      ],
      description: [this.editProduct ? this.editProduct.description : '', Validators.required],
      price: [
        !this.isAddDrug && this.editProduct ? 
        (this.editProduct as MedicalTest).price : '', 
        this.isAddDrug ? [] :
        [Validators.required, Validators.pattern('[0-9]*')]
      ],
      partnerPrice: [
        !this.isAddDrug && this.editProduct ? 
        (this.editProduct as MedicalTest).partner_price : '', 
        this.isAddDrug ? [] :
        [Validators.required, Validators.pattern('[0-9]*')]
      ],
      category: [this.editProduct ? this.editProduct.category : '', Validators.required],
      duration: [
        !this.isAddDrug && duration ? duration[1] : '',
        this.isAddDrug ? [] : Validators.required
      ],
      time: [
        !this.isAddDrug && duration ? duration[0] : '',
        this.isAddDrug ? [] : Validators.required
      ],
      country_of_origin: [this.isAddDrug && this.editProduct
        ? (this.editProduct as Drug).country_of_origin
        : '',
        this.isAddDrug ? Validators.required : []
      ],
      composition: [this.isAddDrug && this.editProduct
        ? (this.editProduct as Drug).composition
        : '',
        this.isAddDrug ? Validators.required : []
      ],
      // TODO: temporarily removed until client decides
      // expiry_date: [this.isAddDrug && this.editProduct
      //   ? (this.editProduct as Drug).expiry_date
      //   : '',
      //   this.isAddDrug ? Validators.required : []
      // ],
    });
    // only add and populate quantityTypes if this is a drug
    if (this.isAddDrug) {
      this.addProductForm.addControl('quantityTypes', this.formBuilder.array([]));
      if (this.editProduct && (this.editProduct as Drug).quantity_types?.length) {
        (this.editProduct as Drug).quantity_types?.forEach(qty => this.addQuantityType(qty));
      } else  {
        this.addQuantityType();
      }
    }
  }

  get f() {
    return this.addProductForm.controls;
  }

  get quantityTypes(): FormArray {
    return this.addProductForm.get('quantityTypes') as FormArray;
  }

  addQuantityType(existingData?: any) {
    this.quantityTypes.push(this.formBuilder.group({
      id: [existingData?.id ],
      name: [existingData?.name || '', Validators.required], 
      customType: [''],
      selling_price: [existingData?.selling_price || '', [Validators.required, Validators.min(1)]], 
      partner_price: [existingData?.partner_price || '', [Validators.required, Validators.min(1)]],
      is_in_stock: [
        existingData
          ? (existingData.is_in_stock === 1 ? 'yes' : 'no')
          : '',
        Validators.required
      ]
    }));
  }

  removeQuantityType(index: number) {
    this.quantityTypes.removeAt(index);
  }

  onImageChange(event: any): void {
    this.documentService.onImageChange(
      event,
      this.imageCopperElement,
      this.isAllowedImage,
      (isAllowedImage: boolean, imageChangedEvent: any, croppedImage: string) => {
        this.isAllowedImage = isAllowedImage;
        this.imageChangedEvent = imageChangedEvent;
        if (isAllowedImage) {
          this.croppedImages.push(croppedImage);
        }
      });
  }

  onCropImage(croppedImage: string) {
    this.documentService.onCropImage(
      croppedImage, (uploadedImage: File) => 
      this.uploadedImages.push(uploadedImage)
    );
    this.croppedImages.push(croppedImage);
    this.isAllowedImage = true;
  }

  onRemoveImge(index: number, isExistingImage: boolean = false) {
    if (isExistingImage && this.editProduct && this.editProduct.images) {
      const removedImage = this.editProduct.images[index];
      this.editProduct.images.splice(index, 1);
      this.imagesToDelete.push(removedImage);
    } else {
      this.croppedImages.splice(index, 1);
    }
  }
  
  ngOnDestroy() {
    const bodyElement = document.querySelector('.body');
    if (bodyElement) {
      bodyElement.classList.remove('is-not-overflow');
    } else {
      console.error('Body element not found.');
    }
  }

  onSubmit() {
    const formData = new FormData();
    this.submitted = true;
    if (this.addProductForm.invalid) {
      return;
    }
    for (let field in this.addProductForm.value) {
      formData.append(field, this.f[field].value);
    }
    formData.append('duration', `${this.f.time.value} ${this.f.duration.value}`);
    if (this.editProduct && this.isAddDrug) {
      const quantityTypes = this.f.quantityTypes.value.map((quantityType: QuantityDetails) => {
      const item: QuantityDetails = {};
      item.id = quantityType.id ? quantityType.id : 0; // New quantity type
      item.drug_id = this.editProduct.id;
      item.name = quantityType.name;
      item.is_in_stock = quantityType.is_in_stock === 'yes' ? 1 : 0;
      item.selling_price = quantityType.selling_price;
      item.partner_price = quantityType.partner_price;
      return item;
      });
      quantityTypes.forEach((quantity: QuantityDetails) => {
        formData.append('quantity_types[]', JSON.stringify(quantity));
      });
    } else if (this.isAddDrug && this.f.quantityTypes.value && this.f.quantityTypes.value.length > 0) {
      const quantityTypes = this.f.quantityTypes.value.map((quantityType: any) => ({
        name: quantityType.name === 'other' ? quantityType.customType : quantityType.name,
        is_in_stock: quantityType.is_in_stock === 'yes' ? 1 : 0,
        selling_price: quantityType.selling_price,
        partner_price: quantityType.partner_price,
      }));
      quantityTypes.forEach((quantity: QuantityDetails) => {
        formData.append('quantity_types[]', JSON.stringify(quantity));
      });
    }
    if (this.editProduct && this.editProduct.images) {
      if (this.imagesToDelete.length > 0) {
        formData.append('imagesToDelete', [this.imagesToDelete.map(image => image)].toString());
      }
      if (this.uploadedImages.length <= 0) {
        formData.append('images[]', JSON.stringify([]));
      }
    } 
    this.uploadedImages.forEach(image => formData.append('images[]', image));
    const popUpConfig: SweetAlertOptions = {
      title: '',
      html: 'Product Posted',
      icon: 'success',
      confirmButtonColor: '#00ACB1'
    };
    // TODO: check type of product to edit
    if (this.isAddDrug) {
      if (this.editProduct && (this.editProduct as Drug).name) {
        this.pharmacyService.editDrug((this.editProduct as Drug).id, formData)
          .pipe(first()).subscribe(response => {
            if ((response as any)?.code === 500) {
              popUpConfig.title = 'An error occurred';
              popUpConfig.html = 'Please contact us at: <b>info@meshincentre.com</b>';
              popUpConfig.icon = 'error';
              popUpConfig.cancelButtonColor = '#f37b7b';
              Swal.fire(popUpConfig);
              return;
            }
            popUpConfig.html = 'Drug Edited';
            Swal.fire(popUpConfig).then(() => {
              this.dataService.saveLocalDataToView(response);
              this.dataService.clearProduct();
              this.router.navigate(['single-drug-details']);
            });
          });
          
      } else {
        this.pharmacyService.postDrug(formData).pipe(first())
          .subscribe(response => {
            if ((response as any)?.code === 500) {
              popUpConfig.title = 'An error occurred';
              popUpConfig.html = 'Please contact us at: <b>info@meshincentre.com</b>';
              popUpConfig.icon = 'error';
              popUpConfig.cancelButtonColor = '#f37b7b';
              Swal.fire(popUpConfig);
              return;
            }
            Swal.fire(popUpConfig).then(() => {
              this.dataService.saveLocalDataToView(response);
              this.router.navigate(['single-drug-details']);
            });
          });
      }

    } else {
      // Medical Test logic
      if (this.editProduct && (this.editProduct as MedicalTest).name) {
        this.medicalTestService.editMedicalTest((this.editProduct as MedicalTest).id, formData)
          .pipe(first()).subscribe(medicalTest => {
            popUpConfig.html = 'Medical Test Edited';
            Swal.fire(popUpConfig).then(() => {
              this.dataService.saveLocalDataToView(medicalTest);
              this.dataService.clearProduct();
              this.router.navigate(['medical-test-details']);
            });
          }, error => {
            popUpConfig.title = 'An error occurred';
            popUpConfig.html = 'Please contact us at: <b>info@meshincentre.com</b>';
            popUpConfig.icon = 'error';
            popUpConfig.cancelButtonColor = '#f37b7b';
            Swal.fire(popUpConfig);
          });
      } else {
        this.medicalTestService.postMedicalTest(formData).pipe(first())
          .subscribe(response => {
            if ((response as any)?.code === 500) {
              popUpConfig.title = 'An error occurred';
              popUpConfig.html = 'Please contact us at: <b>info@meshincentre.com</b>';
              popUpConfig.icon = 'error';
              popUpConfig.cancelButtonColor = '#f37b7b';
              Swal.fire(popUpConfig);
              return;
            }
            Swal.fire(popUpConfig).then(() => {
              this.dataService.saveLocalDataToView(response);
              this.router.navigate(['medical-test-details']);
            });
          });
      }
    }    
  }
}

